<template>
  <v-card class="sv">
    <header class="sv__head">
      <div class="sv__head-text">
        <p class="sv__eyebrow">Contabilidad</p>
        <h2 class="sv__title">Facturación por servicios</h2>
        <!-- El consolidado se nombra por su CONS-, no por el control F&D
             viejo (LN…) que trae invoiceNumber; la individual, por su
             número de factura y su IND-. -->
        <p class="sv__scope">
          <template v-if="item.consolidated">
            Consolidado
            <strong>{{ item.internalNumber || item.invoiceNumber }}</strong>
          </template>
          <template v-else>
            Factura <strong>{{ item.invoiceNumber }}</strong>
            <span v-if="item.internalNumber"> · {{ item.internalNumber }}</span>
          </template>
          · CIF USD {{ money(item.cif) }}
        </p>
      </div>
      <button type="button" class="sv__close" title="Cerrar" @click="close">
        <v-icon small>close</v-icon>
      </button>
    </header>

    <div class="sv__body">
      <!-- Datos que alimentan el cálculo -->
      <div class="sv__inputs">
        <div class="sv__field">
          <label class="sv__label">Formularios</label>
          <input
            v-model="cfg.formsQuantity"
            type="text"
            inputmode="numeric"
            class="sv__input"
            placeholder="0"
            @change="saveConfig('formsQuantity', cfg.formsQuantity)"
          />
        </div>
        <div class="sv__field">
          <label class="sv__label">Líneas aforadas</label>
          <input
            v-model="cfg.aforoLines"
            type="text"
            inputmode="numeric"
            class="sv__input"
            placeholder="1"
            @change="saveConfig('aforoLines', cfg.aforoLines)"
          />
        </div>
        <button
          class="sv__gen"
          :disabled="generating || savingCfg"
          @click="generate"
        >
          <v-icon small class="sv__gen-icon">calculate</v-icon>
          {{ generating ? 'Generando…' : 'Generar líneas' }}
        </button>
        <p class="sv__help sv__help--wide">
          Vuelve a aplicar la tarifa vigente al corretaje, formularios, líneas y
          conceptos de tarifa fija. Para un precio especial en este trámite,
          ajústalo en la línea después de generar.
        </p>
      </div>

      <!-- Líneas facturables -->
      <p v-if="loading" class="sv__help">Cargando…</p>
      <table v-else class="sv__table">
        <thead>
          <tr>
            <th>Concepto</th>
            <th class="sv__right">Cant.</th>
            <th class="sv__right">Precio</th>
            <th class="sv__right">Monto</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="line in items" :key="line.id">
            <td>
              {{ line.concept.name }}
              <span v-if="isAuto(line)" class="sv__auto">automático</span>
              <span v-if="!line.taxable" class="sv__exempt">exento</span>
              <div v-if="line.description" class="sv__desc">
                {{ line.description }}
              </div>
            </td>
            <td class="sv__right">
              <input
                type="text"
                class="sv__cell"
                inputmode="decimal"
                :value="line.quantity"
                :readonly="isAuto(line)"
                @change="saveLine(line, 'quantity', $event.target.value)"
              />
            </td>
            <td class="sv__right">
              <input
                type="text"
                class="sv__cell"
                inputmode="decimal"
                :value="line.unitAmount"
                :readonly="isAuto(line)"
                @change="saveLine(line, 'unitAmount', $event.target.value)"
              />
            </td>
            <td class="sv__right sv__amount">{{ money(line.amount) }}</td>
            <td class="sv__right">
              <button
                v-if="!isAuto(line)"
                class="sv__icon-btn"
                title="Eliminar esta línea"
                @click="remove(line)"
              >
                <v-icon small>delete_outline</v-icon>
              </button>
            </td>
          </tr>
          <tr v-if="!items.length">
            <td colspan="5" class="sv__empty">
              Sin líneas. Usa «Generar líneas» o agrega un concepto.
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Agregar concepto de tarifa fija -->
      <div class="sv__add">
        <v-select
          v-model="nuevo.conceptId"
          :items="selectable"
          item-text="name"
          item-value="id"
          placeholder="Agregar concepto…"
          no-data-text="Sin conceptos disponibles"
          solo
          flat
          hide-details
          class="sv__select"
        ></v-select>
        <input
          v-model="nuevo.quantity"
          type="text"
          inputmode="decimal"
          class="sv__cell"
          placeholder="1"
        />
        <input
          v-model="nuevo.unitAmount"
          type="text"
          inputmode="decimal"
          class="sv__cell"
          placeholder="Tarifa"
        />
        <button
          class="sv__btn sv__btn--add"
          :disabled="!nuevo.conceptId || adding"
          @click="add"
        >
          <v-icon small class="sv__btn-icon">add</v-icon>
          Agregar
        </button>
      </div>

      <!-- Totales -->
      <div class="sv__totals">
        <div class="sv__total-row">
          <span>Sub-Total</span>
          <strong>B/. {{ money(totals.subtotal) }}</strong>
        </div>
        <div class="sv__total-row sv__total-row--muted">
          <span>Base gravada</span>
          <span>B/. {{ money(totals.taxableBase) }}</span>
        </div>
        <div class="sv__total-row">
          <span>I.T.B.M.S. ({{ itbmsRateLabel }}%)</span>
          <strong>B/. {{ money(totals.itbms) }}</strong>
        </div>
        <div class="sv__total-row sv__retention">
          <label class="sv__switch">
            <input
              v-model="cfg.itbmsRetention"
              type="checkbox"
              @change="saveConfig('itbmsRetention', cfg.itbmsRetention)"
            />
            Retención del 50% del I.T.B.M.S.
          </label>
          <strong v-if="totals.retained"
            >− B/. {{ money(totals.retained) }}</strong
          >
          <span v-else class="sv__muted">—</span>
        </div>
        <div class="sv__total-row sv__total-row--grand">
          <span>Facturación total</span>
          <strong>B/. {{ money(totals.total) }}</strong>
        </div>
      </div>

      <!-- Factura de servicios de Alegra: su N° (manual) y su fecha. El
           estado de cuenta lista, por trámite, el N° Alegra, la fecha y el
           monto; sin el N° no se puede emitir. La fecha la sella el primer
           comprobante y aquí solo se corrige: es la que define el mes del
           estado de cuenta. Se guardan al salir del campo. -->
      <section class="sv__docs">
        <p class="sv__docs-label">
          Factura de servicios (Alegra)
          <span
            v-if="item.serviceInvoiceDate && item.serviceInvoiceNumber"
            class="sv__alegra-state sv__alegra-state--ok"
          >
            Facturado el {{ dateOf(item.serviceInvoiceDate) }}
          </span>
          <span
            v-else-if="item.serviceInvoiceDate"
            class="sv__alegra-state sv__alegra-state--pending"
          >
            Falta el N° de factura Alegra
          </span>
          <span v-else class="sv__alegra-state sv__alegra-state--pending">
            Pendiente: adjunta el comprobante
            <template v-if="!item.serviceInvoiceNumber">
              y escribe el N° de factura
            </template>
          </span>
        </p>
        <div class="sv__inputs sv__inputs--alegra">
          <div class="sv__field">
            <label class="sv__label">N° factura Alegra</label>
            <input
              type="text"
              class="sv__input sv__input--wide"
              placeholder="N° de factura Alegra"
              maxlength="40"
              :value="alegraValue('serviceInvoiceNumber')"
              :readonly="alegra.serviceInvoiceNumber.saving"
              :class="{
                'sv__input--error': alegra.serviceInvoiceNumber.error,
              }"
              @input="
                onAlegraInput('serviceInvoiceNumber', $event.target.value)
              "
              @keyup.enter="$event.target.blur()"
              @keyup.esc="cancelAlegra('serviceInvoiceNumber', $event)"
              @blur="saveAlegra('serviceInvoiceNumber')"
            />
          </div>
          <div v-if="item.serviceInvoiceDate" class="sv__field">
            <label class="sv__label">Fecha de facturación</label>
            <input
              type="date"
              class="sv__input sv__input--date"
              :max="today"
              :value="alegraValue('serviceInvoiceDate')"
              :readonly="alegra.serviceInvoiceDate.saving"
              :class="{ 'sv__input--error': alegra.serviceInvoiceDate.error }"
              @change="
                saveAlegraDate('serviceInvoiceDate', $event.target.value)
              "
            />
          </div>
          <span v-if="alegraSaving" class="sv__alegra-status">Guardando…</span>
          <span v-else-if="alegraError" class="sv__alegra-error">
            {{ alegraError }}
          </span>
          <!-- El N° repetido solo se avisa: casi siempre es un error de
               tecleo, pero una misma factura de Alegra podría cubrir dos
               trámites, así que no se bloquea nada. -->
          <span
            v-else-if="alegraDuplicateText"
            class="sv__alegra-warn"
            :title="alegraDuplicateTitle"
          >
            {{ alegraDuplicateText }}
          </span>
        </div>
        <p v-if="item.serviceInvoiceDate" class="sv__help">
          Es la fecha que define el mes del estado de cuenta.
        </p>

        <!-- Comprobantes: el cliente quiere ver lo cargado, eliminarlo y
             reemplazarlo cuantas veces haga falta, sin salir del detalle. -->
        <p class="sv__docs-label sv__docs-label--sub">
          Comprobantes cargados
          <span class="sv__count">{{ documents.length }}</span>
        </p>
        <p v-if="loadingDocs" class="sv__help">Cargando…</p>
        <p v-else-if="!documents.length" class="sv__help">
          Todavía no hay comprobante de la factura de servicios. Al subir el
          primero se registra la fecha de facturación.
        </p>
        <ul v-else class="sv__doc-list">
          <li v-for="doc in documents" :key="doc.id" class="sv__doc">
            <span class="sv__doc-date">{{ dateOf(doc.createdAt) }}</span>
            <a
              :href="fullPath(doc.filePath)"
              target="_blank"
              rel="noopener"
              class="sv__doc-name"
              :title="doc.originalName || doc.filePath"
            >
              {{ doc.originalName || doc.filePath }}
            </a>
            <span v-if="doc.uploadedBy" class="sv__doc-who">
              {{ doc.uploadedBy.firstName }} {{ doc.uploadedBy.lastName }}
            </span>
            <template v-if="confirming === doc.id">
              <span class="sv__confirm">¿Eliminar?</span>
              <button
                class="sv__mini sv__mini--danger"
                :disabled="deleting"
                @click="doDelete(doc)"
              >
                Sí, eliminar
              </button>
              <button class="sv__mini" @click="confirming = null">
                Cancelar
              </button>
            </template>
            <button
              v-else
              class="sv__icon-btn"
              title="Eliminar este comprobante"
              @click="confirming = doc.id"
            >
              <v-icon small>delete_outline</v-icon>
            </button>
          </li>
        </ul>
        <p v-if="documents.length" class="sv__help">
          Para reemplazar uno, sube el nuevo y elimina el anterior.
        </p>
      </section>

      <p v-if="error" class="sv__error">{{ error }}</p>
      <p v-else-if="notice" class="sv__notice">{{ notice }}</p>

      <input
        ref="file"
        type="file"
        multiple
        style="display: none"
        @change="upload"
      />
    </div>

    <footer class="sv__foot">
      <label class="sv__notify">
        <input v-model="notifyClient" type="checkbox" />
        Avisar al cliente
      </label>
      <button class="sv__btn sv__btn--ghost" @click="close">
        Cerrar
      </button>
      <button class="sv__btn sv__btn--save" :disabled="uploading" @click="pick">
        <v-icon small class="sv__btn-icon">attach_file</v-icon>
        {{ uploading ? 'Subiendo…' : 'Adjuntar comprobante' }}
      </button>
    </footer>
  </v-card>
</template>

<script>
import moment from 'moment-timezone'
import { getFullPath } from '@/helpers/TransitHelper'

export default {
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    items: [],
    concepts: [],
    totals: {
      subtotal: 0,
      taxableBase: 0,
      itbms: 0,
      retained: 0,
      total: 0,
    },
    cfg: {
      formsQuantity: '',
      aforoLines: '',
      itbmsRetention: false,
    },
    loading: true,
    generating: false,
    savingCfg: false,
    adding: false,
    uploading: false,
    notifyClient: false,
    error: '',
    notice: '',
    nuevo: { conceptId: null, quantity: '', unitAmount: '' },
    // Comprobantes de la factura de servicios (paso SERVICE).
    documents: [],
    loadingDocs: false,
    confirming: null,
    deleting: false,
    // Borrador y estado de guardado del N° y la fecha de la factura Alegra.
    alegra: {
      serviceInvoiceNumber: { draft: null, saving: false, error: '' },
      serviceInvoiceDate: { draft: null, saving: false, error: '' },
    },
    // Otros trámites que ya tienen este mismo N° de factura Alegra.
    alegraDuplicates: [],
  }),
  computed: {
    // La tasa no se edita aquí: es la del trámite (7% por defecto). Cambiarla
    // a mano en cada factura era un riesgo que nadie pidió.
    itbmsRateLabel() {
      const rate = parseFloat(this.item.itbmsRate)
      return Number.isNaN(rate) ? 7 : rate
    },
    today() {
      return moment().format('YYYY-MM-DD')
    },
    alegraSaving() {
      return Object.keys(this.alegra).some(f => this.alegra[f].saving)
    },
    alegraError() {
      const field = Object.keys(this.alegra).find(f => this.alegra[f].error)
      return field ? this.alegra[field].error : ''
    },
    // Se nombran dos trámites y el resto se cuenta: la lista completa va en
    // el title, para no romper la línea de campos.
    alegraDuplicateText() {
      const nombres = this.alegraDuplicateNames
      if (!nombres.length) return ''
      const resto = nombres.length - 2
      const visibles = nombres.slice(0, 2).join(', ')
      return resto > 0
        ? `Este N° ya está en ${visibles} y ${resto} más`
        : `Este N° ya está en ${visibles}`
    },
    alegraDuplicateTitle() {
      return this.alegraDuplicateNames.join(', ')
    },
    alegraDuplicateNames() {
      return this.alegraDuplicates.map(
        d => d.internalNumber || d.invoiceNumber || `#${d.id}`
      )
    },
    // Solo se agregan a mano los de tarifa fija: los automáticos los genera
    // el botón y ponerlos dos veces duplicaría el cobro.
    selectable() {
      const usados = this.items.map(i => i.concept.id)
      return this.concepts.filter(
        c =>
          (c.calcType === 'FIXED' || c.calcType === 'MANUAL') &&
          usados.indexOf(c.id) === -1
      )
    },
  },
  created() {
    const str = v => (v === null || typeof v === 'undefined' ? '' : String(v))
    this.cfg = {
      formsQuantity: str(this.item.formsQuantity),
      aforoLines: str(this.item.aforoLines),
      itbmsRetention: !!this.item.itbmsRetention,
    }
    this.load()
    this.loadDocuments()
    this.checkAlegraDuplicates()
  },
  methods: {
    fullPath: getFullPath,
    dateOf(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    close() {
      this.$emit('close-billing')
    },
    money(value) {
      const n = parseFloat(value)
      if (Number.isNaN(n)) return '0.00'
      return n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    // Las líneas de cálculo automático no se editan a mano: se regeneran.
    isAuto(line) {
      return (
        line.concept &&
        line.concept.calcType !== 'FIXED' &&
        line.concept.calcType !== 'MANUAL'
      )
    },
    load() {
      this.loading = true
      Promise.all([
        this.$store.dispatch('invoices/getServiceItems', {
          ids: String(this.item.id),
        }),
        this.$store.dispatch('invoices/getServiceConcepts'),
      ])
        .then(([rows, concepts]) => {
          const row = (rows || [])[0]
          this.items = (row && row.items) || []
          this.totals = (row && row.totals) || this.totals
          this.concepts = concepts || []
        })
        .finally(() => {
          this.loading = false
        })
    },
    // Las cantidades (formularios, líneas) solo cambian el monto al
    // regenerar, así que al guardarlas se regeneran las líneas automáticas
    // en el acto: antes quedaba el monto viejo hasta pulsar «Generar
    // líneas». La retención cambia los totales directamente.
    // === N° y fecha de la factura Alegra ===
    alegraValue(field) {
      const st = this.alegra[field]
      return st.draft !== null ? st.draft : this.item[field] || ''
    },
    onAlegraInput(field, value) {
      this.alegra[field] = { draft: value, saving: false, error: '' }
    },
    // Escape descarta lo escrito y suelta el foco; el blur que sigue no
    // guarda nada porque ya no hay borrador.
    cancelAlegra(field, event) {
      this.alegra[field] = { draft: null, saving: false, error: '' }
      event.target.value = this.item[field] || ''
      event.target.blur()
    },
    // El selector de fecha entrega el valor completo en change: se guarda ahí.
    saveAlegraDate(field, value) {
      this.onAlegraInput(field, value)
      this.saveAlegra(field)
    },
    // Guarda en el TRÁMITE (item.id es la fila padre en un consolidado). El
    // N° puede vaciarse; la fecha no: sin fecha no hay factura, y quitarla es
    // eliminar los comprobantes.
    saveAlegra(field) {
      const st = this.alegra[field]
      if (st.draft === null || st.saving) return
      const next = String(st.draft).trim()
      const current = this.item[field] || ''
      if (next === current) {
        this.alegra[field] = { draft: null, saving: false, error: '' }
        return
      }
      if (field === 'serviceInvoiceDate' && next === '') {
        this.alegra[field] = {
          draft: null,
          saving: false,
          error: 'La fecha no puede quedar vacía.',
        }
        return
      }
      this.alegra[field] = { draft: next, saving: true, error: '' }
      this.$store
        .dispatch('invoices/updateAccounting', {
          invoiceId: this.item.id,
          [field]: next,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.alegra[field] = {
              draft: next,
              saving: false,
              error: this.errorText(response, 'No se pudo guardar el dato.'),
            }
            return
          }
          // item viene de la fila de la lista: queda al día sin recargar.
          this.$set(this.item, field, next === '' ? null : next)
          this.alegra[field] = { draft: null, saving: false, error: '' }
          this.$emit('changed')
          if (field === 'serviceInvoiceNumber') {
            this.checkAlegraDuplicates()
          }
        })
        .catch(() => {
          this.alegra[field] = {
            draft: next,
            saving: false,
            error: 'No se pudo guardar el dato.',
          }
        })
    },
    // Avisa si el N° ya está en otro trámite. Es secundario: si la consulta
    // falla no se estorba al usuario, el guardado ya ocurrió.
    checkAlegraDuplicates() {
      const value = this.item.serviceInvoiceNumber || ''
      if (!value) {
        this.alegraDuplicates = []
        return
      }
      this.$store
        .dispatch('invoices/checkAlegraNumber', {
          field: 'serviceInvoiceNumber',
          value,
          invoiceId: this.item.id,
        })
        // La acción del store ya devuelve el arreglo de trámites (o [] si la
        // consulta falló), no la respuesta completa.
        .then(duplicates => {
          this.alegraDuplicates = Array.isArray(duplicates) ? duplicates : []
        })
        .catch(() => {
          this.alegraDuplicates = []
        })
    },
    // El backend responde { error: 'mensaje' }; se cubre también `message`.
    errorText(response, fallback) {
      const data = response && response.data
      if (!data) return fallback
      if (typeof data.error === 'string' && data.error) return data.error
      if (typeof data.message === 'string' && data.message) return data.message
      return fallback
    },
    saveConfig(field, value) {
      this.error = ''
      this.notice = ''
      const raw = typeof value === 'string' ? value.trim() : value
      const isQty = field === 'formsQuantity' || field === 'aforoLines'
      if (isQty && raw !== '') {
        const n = parseInt(raw, 10)
        if (Number.isNaN(n) || n < 0) {
          this.error = 'La cantidad debe ser un número entero.'
          return
        }
      }
      this.savingCfg = true
      this.$store
        .dispatch('invoices/updateAccounting', {
          invoiceId: this.item.id,
          [field]: raw === '' ? null : raw,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudo guardar el dato.'
            return null
          }
          if (!isQty) return response
          return this.$store.dispatch('invoices/generateServiceItems', {
            invoiceId: this.item.id,
          })
        })
        .then(response => {
          if (response === null) return
          if (!response || (response.status && response.status >= 400)) {
            this.error =
              'Se guardó la cantidad, pero no se pudieron regenerar las líneas.'
          }
          this.load()
          this.$emit('changed')
        })
        .finally(() => {
          this.savingCfg = false
        })
    },
    loadDocuments() {
      this.loadingDocs = true
      this.$store
        .dispatch('invoices/getDocuments', {
          ids: String(this.item.id),
          step: 'SERVICE',
        })
        .then(docs => {
          this.documents = docs || []
        })
        .finally(() => {
          this.loadingDocs = false
        })
    },
    doDelete(doc) {
      this.error = ''
      this.notice = ''
      this.deleting = true
      this.$store
        .dispatch('invoices/deleteDocument', { id: doc.id })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudo eliminar el comprobante.'
            return
          }
          this.documents = this.documents.filter(d => d.id !== doc.id)
          // Sin comprobantes el backend borra la fecha de facturación y el
          // semáforo de la fila vuelve a rojo; aquí se oculta el campo.
          if (!this.documents.length) {
            this.$set(this.item, 'serviceInvoiceDate', null)
          }
          this.$emit('changed')
        })
        .finally(() => {
          this.deleting = false
          this.confirming = null
        })
    },
    generate() {
      this.error = ''
      this.notice = ''
      this.generating = true
      this.$store
        .dispatch('invoices/generateServiceItems', { invoiceId: this.item.id })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudieron generar las líneas.'
            return
          }
          this.load()
          this.$emit('changed')
        })
        .finally(() => {
          this.generating = false
        })
    },
    add() {
      this.error = ''
      this.adding = true
      this.$store
        .dispatch('invoices/saveServiceItem', {
          invoiceId: this.item.id,
          conceptId: this.nuevo.conceptId,
          quantity: this.nuevo.quantity === '' ? 1 : this.nuevo.quantity,
          unitAmount: this.nuevo.unitAmount,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudo agregar el concepto.'
            return
          }
          this.nuevo = { conceptId: null, quantity: '', unitAmount: '' }
          this.load()
          this.$emit('changed')
        })
        .finally(() => {
          this.adding = false
        })
    },
    saveLine(line, field, raw) {
      const parsed = parseFloat(raw)
      if (Number.isNaN(parsed)) {
        this.error = 'Debe ser un número.'
        return
      }
      this.error = ''
      this.$store
        .dispatch('invoices/saveServiceItem', { id: line.id, [field]: raw })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudo guardar la línea.'
            return
          }
          this.load()
          this.$emit('changed')
        })
    },
    remove(line) {
      this.$store
        .dispatch('invoices/deleteServiceItem', { id: line.id })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudo eliminar la línea.'
            return
          }
          this.load()
          this.$emit('changed')
        })
    },
    pick() {
      this.$refs.file.click()
    },
    upload(e) {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      if (!files.length) return
      this.error = ''
      this.uploading = true
      this.$store
        .dispatch('invoices/uploadServiceDocs', {
          invoiceId: this.item.id,
          files,
          sendEmails: this.notifyClient ? 'true' : 'false',
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudieron subir los comprobantes.'
            return
          }
          // El diálogo se queda abierto para que se vea lo que quedó
          // cargado; la lista de atrás escucha 'changed' (no 'saved', que
          // nadie escuchaba: por eso no refrescaba semáforo ni fecha).
          this.notice = `${files.length} comprobante(s) cargado(s).`
          if (!this.item.serviceInvoiceDate) {
            // El backend sella la fecha con el primer comprobante; se refleja
            // aquí para que aparezca el campo de fecha sin reabrir el diálogo.
            // La lista trae la fecha real al recargar.
            this.$set(
              this.item,
              'serviceInvoiceDate',
              moment().format('YYYY-MM-DD')
            )
          }
          this.loadDocuments()
          this.$emit('changed')
        })
        .finally(() => {
          this.uploading = false
        })
    },
  },
}
</script>

<style scoped>
.sv {
  border-radius: 12px;
  overflow: hidden;
}

/* === Encabezado === */
.sv__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 14px 24px;
  border-left: 4px solid #15803d;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}
.sv__head-text {
  flex: 1;
  min-width: 0;
}
.sv__eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.sv__title {
  margin: 2px 0 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}
.sv__scope {
  margin: 4px 0 0;
  font-size: 14px;
  color: #64748b;
}
.sv__scope strong {
  color: #254f87;
}
.sv__close {
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 4px;
  cursor: pointer;
  line-height: 1;
}
.sv__close:hover {
  background: #f1f5f9;
}
.sv__close .v-icon {
  color: #64748b !important;
}

/* === Cuerpo === */
.sv__body {
  padding: 16px 24px 4px;
  max-height: 64vh;
  overflow-y: auto;
}
.sv__help,
.sv__muted {
  font-size: 13px;
  color: #94a3b8;
}
.sv__help--wide {
  flex-basis: 100%;
  margin: 0;
}
.sv__error {
  margin: 8px 0 0;
  font-size: 13px;
  color: #b91c1c;
}
.sv__notice {
  margin: 8px 0 0;
  font-size: 13px;
  color: #15803d;
}

/* === Entradas del cálculo === */
.sv__inputs {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding-bottom: 14px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
}
.sv__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sv__label {
  font-size: 12.5px;
  font-weight: 600;
  color: #475569;
}
.sv__input {
  width: 110px;
  height: 34px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  color: #0f172a;
  background: #ffffff;
}
.sv__input:focus {
  outline: none;
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12);
}
.sv__gen {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid rgba(37, 79, 135, 0.3);
  background: rgba(37, 79, 135, 0.08);
  color: #254f87;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
}
.sv__gen .sv__gen-icon {
  color: #254f87 !important;
  font-size: 15px !important;
}
.sv__gen:hover:not(:disabled) {
  background: rgba(37, 79, 135, 0.16);
}
.sv__gen:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* === Tabla de líneas === */
.sv__table {
  width: 100%;
  border-collapse: collapse;
}
.sv__table th {
  text-align: left;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  padding: 6px 4px;
  border-bottom: 1px solid #e2e8f0;
}
.sv__table td {
  padding: 8px 4px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 15px;
  color: #334155;
  vertical-align: top;
}
.sv__right {
  text-align: right;
}
.sv__amount {
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}
.sv__desc {
  font-size: 12.5px;
  color: #94a3b8;
  margin-top: 2px;
}
.sv__auto,
.sv__exempt {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.sv__auto {
  background: #dbeafe;
  color: #1e40af;
}
.sv__exempt {
  background: #f1f5f9;
  color: #475569;
}
.sv__cell {
  width: 88px;
  text-align: right;
  height: 30px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  background: #ffffff;
}
.sv__cell:read-only {
  background: #f8fafc;
  color: #64748b;
}
.sv__cell:focus {
  outline: none;
  border-color: #254f87;
}
.sv__empty {
  text-align: center;
  color: #94a3b8;
  padding: 16px 0 !important;
}
.sv__icon-btn {
  border: none;
  background: transparent;
  border-radius: 5px;
  padding: 3px;
  cursor: pointer;
}
.sv__icon-btn:hover {
  background: #fee2e2;
}
.sv__icon-btn .v-icon {
  color: #b91c1c !important;
}

/* === Agregar === */
.sv__add {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;
}
.sv__select {
  flex: 1;
}

/* === Totales === */
.sv__totals {
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}
.sv__total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 15px;
  color: #334155;
  font-variant-numeric: tabular-nums;
}
.sv__total-row--muted {
  color: #94a3b8;
  font-size: 13px;
}
.sv__retention {
  border-top: 1px solid #e2e8f0;
  margin-top: 4px;
  padding-top: 8px;
}
.sv__switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13.5px;
  color: #475569;
}
.sv__total-row--grand {
  border-top: 2px solid #cbd5e1;
  margin-top: 6px;
  padding-top: 10px;
  font-size: 16px;
  color: #0f172a;
}
.sv__total-row--grand strong {
  font-size: 20px;
}

/* === Comprobantes === */
.sv__docs {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}
.sv__docs-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #475569;
}
.sv__docs-label--sub {
  margin-top: 12px;
}
/* Estado de la factura Alegra, junto al título del bloque. */
.sv__alegra-state {
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}
.sv__alegra-state--ok {
  color: #15803d;
}
.sv__alegra-state--pending {
  color: #991b1b;
}
/* N° y fecha de la factura Alegra: mismos campos que arriba, sin la línea
   divisoria de abajo. */
.sv__inputs--alegra {
  padding-bottom: 0;
  margin-bottom: 4px;
  border-bottom: 0;
}
.sv__input--wide {
  width: 200px;
  font-weight: 600;
  font-variant-numeric: normal;
}
.sv__input--date {
  width: 160px;
}
.sv__input--error {
  border-color: #dc2626;
}
.sv__alegra-status {
  padding-bottom: 9px;
  font-size: 12.5px;
  color: #94a3b8;
}
.sv__alegra-error {
  padding-bottom: 9px;
  font-size: 12.5px;
  font-weight: 600;
  color: #b91c1c;
}
/* Ámbar, no rojo: el N° repetido es un aviso, no un error; no impide
   guardar ni emitir el estado de cuenta. */
.sv__alegra-warn {
  padding-bottom: 9px;
  font-size: 12.5px;
  font-weight: 600;
  color: #92400e;
  cursor: help;
}
.sv__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
}
.sv__doc-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.sv__doc {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 15px;
}
.sv__doc-date {
  flex: 0 0 auto;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}
.sv__doc-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #254f87;
  text-decoration: none;
}
.sv__doc-name:hover {
  text-decoration: underline;
}
.sv__doc-who {
  flex: 0 0 auto;
  color: #94a3b8;
  font-size: 13px;
}
.sv__confirm {
  flex: 0 0 auto;
  color: #b91c1c;
  font-weight: 600;
  font-size: 13px;
}
.sv__mini {
  flex: 0 0 auto;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 5px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
}
.sv__mini:hover {
  background: #f1f5f9;
}
.sv__mini--danger {
  border-color: #fecaca;
  color: #b91c1c;
}
.sv__mini--danger:hover {
  background: #fef2f2;
}

/* === Pie === */
.sv__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px 18px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
.sv__notify {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: auto;
  font-size: 13.5px;
  color: #475569;
  cursor: pointer;
}
.sv__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
}
.sv__btn--ghost {
  background: #ffffff;
  border-color: #e2e8f0;
  color: #475569;
}
.sv__btn--ghost:hover {
  background: #f1f5f9;
}
.sv__btn--add {
  background: #254f87;
  border-color: #254f87;
  color: #ffffff;
  height: 34px;
}
.sv__btn--add .sv__btn-icon {
  color: #ffffff !important;
}
.sv__btn--add:hover:not(:disabled) {
  background: #1a3a63;
}
/* Blanco sobre #15803d = 5.02:1, cumple WCAG AA. */
.sv__btn--save {
  background: #15803d;
  border-color: #15803d;
  color: #ffffff;
}
.sv__btn--save .sv__btn-icon {
  color: #ffffff !important;
}
.sv__btn--save:hover:not(:disabled) {
  background: #166534;
}
.sv__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
